import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { IImporter } from '@feathr/blackbox';
import { StatusPillCell, Tooltip } from '@feathr/components';
import RunningProgress, { toRunningPercent } from '@feathr/extender/components/RunningProgress';

interface IImportRunningStatusProps {
  progress: IImporter['progress'];
}

/**
 * The Status cell of a running import: its progress bar, "In progress" and its percent, or the
 * "In progress" chip until the server reports a percent.
 */
function ImportRunningStatus({ progress }: Readonly<IImportRunningStatusProps>): JSX.Element {
  const { t } = useTranslation();
  const percent = toRunningPercent(progress);

  // Placeholder copy, pending Product and Design (decision 23).
  const tooltip =
    percent === undefined
      ? t(
          'This import is running. We will email you when it finishes. If it cannot finish, its status changes to Failed.',
        )
      : t(
          '{{percent}}% of rows read. This import is running. We will email you when it finishes. If it cannot finish, its status changes to Failed.',
          { percent },
        );

  if (percent === undefined) {
    return (
      <Tooltip focusable={true} title={tooltip}>
        <StatusPillCell status={t('In progress')} theme={'warning'} truncate={false} />
      </Tooltip>
    );
  }

  return (
    <RunningProgress
      caption={t('In progress')}
      label={t('Import progress')}
      percent={percent}
      tooltip={tooltip}
    />
  );
}

export default ImportRunningStatus;
